import * as Tabs from '@radix-ui/react-tabs';
import clsx from 'clsx';
import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { mergeProps } from 'src/core/utils';

import { ScrollableToolbar } from '../ScrollableToolbar';
import * as styles from './Tabs.css';

export const Root = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof Tabs.Root>
>(function Root({ ...props }, ref) {
  return (
    <Tabs.Root
      {...props}
      ref={ref}
      {...mergeProps(
        {
          className: clsx(props.className, {
            [styles.tabsRoot]: props.orientation !== 'vertical',
            [styles.tabsRootVertical]: props.orientation === 'vertical',
          }),
        },
        props
      )}
    />
  );
});

export const Trigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof Tabs.Trigger> & {
    hasErrors?: boolean;
    orientation?: 'horizontal' | 'vertical';
    variant?: 'default' | 'pill'; // Add variant prop
  }
>(function Trigger({ hasErrors, orientation, variant, ...props }, ref) {
  return (
    <Tabs.Trigger
      ref={ref}
      {...mergeProps(
        {
          className: clsx({
            [styles.tabsTrigger]: orientation !== 'vertical',
            [styles.tabsTriggerWithErrors]:
              orientation !== 'vertical' && hasErrors,
            [styles.tabsTriggerVertical]: orientation === 'vertical',
            [styles.tabsTriggerWithErrorsVertical]:
              orientation === 'vertical' && hasErrors,

            // Pill variant styles
            [styles.tabsTriggerPill]: variant === 'pill',
            [styles.tabsTriggerPillWithErrors]: variant === 'pill' && hasErrors,
          }),
        },
        props
      )}
    />
  );
});

export const List = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof Tabs.List> & {
    orientation?: 'horizontal' | 'vertical';
    variant?: 'default' | 'pill'; // Add variant prop
    rootClassName?: string;
    showScrollEdges?: boolean;
    showScrollButton?: boolean;
    scrollButtonClassName?: Record<'up' | 'down' | 'left' | 'right', string>;
  }
>(function List(
  {
    orientation,
    variant = 'default',
    showScrollEdges,
    showScrollButton,
    rootClassName,
    scrollButtonClassName,
    ...props
  },
  ref
) {
  return (
    <ScrollableToolbar
      className={clsx(
        {
          [styles.tabListContainer]: orientation !== 'vertical',
          [styles.tabListContainerVertical]: orientation === 'vertical',
          // Pill variant container styles
          [styles.tabListContainerPill]: variant === 'pill',
        },
        rootClassName
      )}
      showScrollEdges={showScrollEdges}
      showScrollButton={showScrollButton}
      scrollButtonClassName={scrollButtonClassName}
    >
      <Tabs.List
        ref={ref}
        {...mergeProps(
          {
            className: clsx(props.className, {
              [styles.tabsList]: orientation !== 'vertical',
              [styles.tabsListVertical]: orientation === 'vertical',

              // Pill variant list styles
              [styles.tabsListPill]: variant === 'pill',
            }),
          },
          props
        )}
      />
    </ScrollableToolbar>
  );
});

export const Content = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof Tabs.Content> & {
    orientation?: 'horizontal' | 'vertical';
  }
>(function Content({ orientation, ...props }, ref) {
  return (
    <Tabs.Content
      ref={ref}
      {...mergeProps(
        { style: orientation === 'vertical' ? { width: '100%' } : {} },
        props
      )}
    />
  );
});
